// Reward offer tracking — all data lives in PHP JSON files on Hostinger.
// No RPC event scanning. Browsers that completed an on-chain action during an
// active offer window POST a record to the server from the receipt-success path.

import { useEffect, useState } from "react";
import { getStoredApiKey } from "./useAnnouncement";

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

// ============================================================
// Fire-and-forget event recorder — called from useTx/useAdminTx
// after a tx receipt lands. Returns a promise but callers can
// ignore it; failure is silent (user shouldn't be blocked).
// ============================================================
export async function recordOfferEvent({ wallet, userId, name, action, packageId, amount, txHash }) {
  if (!wallet || !action) return;
  try {
    await fetch(`${API_BASE}/offer-events.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        wallet: wallet.toLowerCase(),
        userId: userId ? String(userId) : "",
        name: name || "",
        action,
        packageId: packageId || 0,
        amount: amount || "",
        txHash: txHash || "",
      }),
      keepalive: true,
    });
  } catch {
    // Silent — user's on-chain action succeeded; off-chain log miss is tolerable.
  }
}

// ============================================================
// Offers CRUD (admin)
// ============================================================
export async function fetchOffers({ activeOnly = false } = {}) {
  try {
    const qs = activeOnly ? "?active=1" : "";
    const r = await fetch(`${API_BASE}/offers.php${qs}`, { cache: "no-store" });
    if (!r.ok) return [];
    const d = await r.json();
    return Array.isArray(d) ? d : [];
  } catch { return []; }
}

export async function saveOffer({ id, name, startTs, endTs, rewardLabel }) {
  const key = getStoredApiKey();
  if (!key) return { ok: false, error: "No API key set" };
  try {
    const r = await fetch(`${API_BASE}/offers.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Admin-Key": key },
      body: JSON.stringify({ id, name, startTs, endTs, rewardLabel }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, error: d?.error || `HTTP ${r.status}` };
    return { ok: true, offers: d.offers || [] };
  } catch (e) { return { ok: false, error: e.message }; }
}

export async function deleteOffer(id) {
  const key = getStoredApiKey();
  if (!key) return { ok: false, error: "No API key set" };
  try {
    const r = await fetch(`${API_BASE}/offers.php?id=${id}`, {
      method: "DELETE",
      headers: { "X-Admin-Key": key },
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      return { ok: false, error: d?.error || `HTTP ${r.status}` };
    }
    return { ok: true };
  } catch (e) { return { ok: false, error: e.message }; }
}

// ============================================================
// Qualifying users for an offer (admin)
// ============================================================
export async function fetchOfferEvents(offerId, { dedupe = true } = {}) {
  const key = getStoredApiKey();
  if (!key) return [];
  try {
    const r = await fetch(`${API_BASE}/offer-events.php?offerId=${offerId}&dedupe=${dedupe ? 1 : 0}`, {
      headers: { "X-Admin-Key": key },
    });
    if (!r.ok) return [];
    return await r.json();
  } catch { return []; }
}

// ============================================================
// Rewards tracking
// ============================================================
export async function fetchRewardsForOffer(offerId) {
  const key = getStoredApiKey();
  if (!key) return [];
  try {
    const r = await fetch(`${API_BASE}/offer-rewards.php?offerId=${offerId}`, {
      headers: { "X-Admin-Key": key },
    });
    if (!r.ok) return [];
    return await r.json();
  } catch { return []; }
}

export async function markRewarded({ offerId, wallet, userId, paidTxHash, amount, note }) {
  const key = getStoredApiKey();
  if (!key) return { ok: false, error: "No API key set" };
  try {
    const r = await fetch(`${API_BASE}/offer-rewards.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Admin-Key": key },
      body: JSON.stringify({ offerId, wallet: wallet.toLowerCase(), userId, paidTxHash, amount, note }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return { ok: false, error: d?.error || `HTTP ${r.status}` };
    return { ok: true };
  } catch (e) { return { ok: false, error: e.message }; }
}

export async function unmarkRewarded({ offerId, wallet }) {
  const key = getStoredApiKey();
  if (!key) return { ok: false, error: "No API key set" };
  try {
    const r = await fetch(`${API_BASE}/offer-rewards.php?offerId=${offerId}&wallet=${wallet.toLowerCase()}`, {
      method: "DELETE",
      headers: { "X-Admin-Key": key },
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      return { ok: false, error: d?.error || `HTTP ${r.status}` };
    }
    return { ok: true };
  } catch (e) { return { ok: false, error: e.message }; }
}

// ============================================================
// User-side: fetch rewards for a specific wallet (public)
// ============================================================
export function useMyRewards(wallet) {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wallet) { setRewards([]); return; }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_BASE}/offer-rewards.php?wallet=${wallet.toLowerCase()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (!cancelled) setRewards(Array.isArray(d) ? d : []);
      } catch {
        if (!cancelled) setRewards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [wallet]);

  return { rewards, loading };
}
